
import React from 'react';
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Linkedin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const AboutUs = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Business Overview */}
        <section className="section-padding bg-gradient-to-b from-sky-50 to-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-careblue-800">About CountedCare</h1>
              <p className="text-xl text-muted-foreground mb-8">
                We're building intelligent financial tools to help family caregivers navigate the complex landscape of caregiving expenses and maximize tax deductions.
              </p>
              
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold mb-4 text-careblue-700">What We Do</h2>
                  <p className="text-lg text-muted-foreground mb-4">
                    CountedCare is a FinTech solution designed for the 53 million Americans caring for aging family members. 
                    We provide an intelligent expense tracking platform that helps caregivers:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 mt-0.5 text-sky-700 shrink-0" />
                      <span className="text-muted-foreground">Capture and categorize medical expenses using AI-powered receipt scanning</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 mt-0.5 text-sky-700 shrink-0" />
                      <span className="text-muted-foreground">Track medical mileage with GPS integration and automatic IRS rate calculations</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 mt-0.5 text-sky-700 shrink-0" />
                      <span className="text-muted-foreground">Monitor progress toward the IRS 7.5% AGI threshold for medical expense deductions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 mt-0.5 text-sky-700 shrink-0" />
                      <span className="text-muted-foreground">Access AI-powered guidance on IRS Publication 502 qualified expenses</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold mb-4 text-careblue-700">The Problem We Solve</h2>
                  <p className="text-lg text-muted-foreground mb-4">
                    <strong>Target Audience:</strong> Working-age family caregivers (ages 35-65) managing care for aging parents or relatives while balancing careers and personal finances.
                  </p>
                  <p className="text-lg text-muted-foreground mb-4">
                    <strong>The Challenge:</strong> Family caregivers spend an average of $7,000+ annually on out-of-pocket care costs—representing 26% of their income. 
                    Despite these significant expenses, most caregivers miss substantial tax deductions because they don't track medical expenses throughout the year or understand what qualifies under IRS rules.
                  </p>
                  <p className="text-lg text-muted-foreground">
                    <strong>Our Solution:</strong> CountedCare automates expense tracking, categorization, and tax deduction calculations, helping caregivers recover thousands of dollars in potential tax savings while reducing financial stress.
                  </p>
                </div>

                <div>
                  <h2 className="text-2xl font-bold mb-4 text-careblue-700">Industry Context</h2>
                  <p className="text-lg text-muted-foreground">
                    We operate at the intersection of FinTech, HealthTech, and Tax Technology. The elder care market is experiencing rapid growth as the 65+ population is projected to reach 95 million by 2060. 
                    Traditional expense tracking apps aren't designed for the unique needs of caregivers, and tax preparation software only helps at year-end—missing the opportunity for real-time financial planning.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="section-padding bg-neutral">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <Badge className="mb-4 bg-carewarm-100 text-carewarm-600 hover:bg-carewarm-200">Our Team</Badge>
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-careblue-800">Meet the Founder</h2>
                <p className="text-lg text-muted-foreground">
                  Built by a caregiver, for caregivers
                </p>
              </div>

              {/* Founder Introduction Video */}
              <div className="mb-12 max-w-2xl mx-auto">
                <div className="flex justify-center">
                  <div className="relative rounded-xl overflow-hidden shadow-lg w-full" style={{ maxWidth: '352px', aspectRatio: '352/625' }}>
                    <iframe 
                      className="absolute top-0 left-0 w-full h-full"
                      src="https://www.youtube.com/embed/qpqA6iZ6TMI" 
                      title="The CountedCare Story" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                      referrerPolicy="strict-origin-when-cross-origin" 
                      allowFullScreen
                    />
                  </div>
                </div>
              </div>

              {/* Founder Bio */}
              <div className="bg-white rounded-xl p-8 shadow-sm border">
                <h3 className="text-xl font-bold mb-4 text-careblue-800">Loryn Carter</h3>
                <p className="text-sm text-muted-foreground mb-2 font-semibold">Founder & CEO</p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  A UCLA Anderson MBA, former Division I athlete, CPG founder, and caregiver herself, Loryn created CountedCare to ease the financial and emotional strain caregiving often brings. Before CountedCare, she spearheaded marketing strategy initiatives at CVS Health and launched the wellness brand VITAVATE. Today, she's focused on expanding CountedCare into a mobile app to bring financial relief to millions of caregivers nationwide.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full md:w-auto"
                  onClick={() => window.open('https://www.linkedin.com/in/loryncarter/', '_blank')}
                >
                  <Linkedin className="h-4 w-4 mr-2" />
                  Connect on LinkedIn
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutUs;
